// Rol Semanal EN FIRME (sector bananero — cliente ABANSER). Nómina → Rol de Pagos.
// A diferencia del Desprendible semanal (informativo/estimado, Desprendible.tsx),
// esta pantalla es el rol de pago semanal DEFINITIVO: genera la semana, permite
// editar días/anticipo/préstamo mientras está en borrador, cierra, contabiliza
// (asiento por etapa productiva) y paga. El motor y las reglas viven en la BD
// (nom_rol_semanal_* — RPCs ya existentes, profile `nomina`).
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  ArrowLeft, BookOpen, Calendar, ChevronDown, ChevronRight, ClipboardList, Download, Filter,
  FileSpreadsheet, Layers, Loader2, Lock, Plus, Printer, RotateCcw, Upload, Wallet, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { usePermissions } from '@/lib/hooks/usePermissions';
import {
  Campo, Modal, fieldCls, hoyISO, money, nomRpc, useSort, SortTh, useDialogoEnLinea,
  type RolRubro,
} from './shared';
import { EstadoRol } from './Rol';

// ------------------------------------------------------------------ Tipos ----
interface RolSemanaRow {
  id: string; codigo: string | null; anio: number; mes: number; semana: number;
  fecha_desde: string; fecha_hasta: string; estado: 'borrador' | 'cerrado' | 'pagado';
  n_empleados: number; total_ingresos: number; total_egresos: number; total_neto: number;
  fecha_generado: string | null; fecha_cerrado: string | null; fecha_pagado: string | null;
}
interface RolSemEmpleado {
  id: string; empleado_id: string; empleado_codigo: string | null; razon_social: string; identificacion: string;
  cargo: string | null; departamento: string | null; dias: number; anticipo: number; prestamo: number;
  sueldo_base: number; ingresos: number; egresos: number; neto: number; rubros: RolRubro[];
}
interface AsientoRef { id: string; numero: number; fecha: string }
interface RolSemDetalle { rol: RolSemanaRow | null; asiento?: AsientoRef | null; empleados: RolSemEmpleado[] }
interface ConsolidadoEmpleado {
  empleado_id: string; nombre: string; cedula: string | null; semanas: number; ganado: number;
  iess_personal: number; iess_patronal: number; ingresos: number; egresos: number; neto: number;
  centro_costo?: string | null;
}
interface ConsolidadoData {
  anio: number; mes: number; empleados: ConsolidadoEmpleado[];
  totales: { ganado: number; iess_personal: number; iess_patronal: number; neto: number };
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const rub = (rubros: RolRubro[], codigo: string) => rubros.find((r) => r.rubro_codigo === codigo)?.valor ?? 0;

// -------------------------------------------------- Filtro por columna (chico, local a este archivo) --
// Normaliza (minúsculas + sin tildes) para comparar "contiene" sin distinguir mayúsculas/acentos.
const normaliza = (v: unknown) => String(v ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
// Encabezado de Excel/CSV \u2192 clave comparable (MAY\u00daSCULAS sin tildes): 'C\u00e9dula'\u2192'CEDULA', 'D\u00edas'\u2192'DIAS'.
const normEncabezado = (v: unknown) => String(v ?? '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

interface AsistenciaResultado { actualizados: number; no_encontrados: string[]; dias_periodo: number }

// `texto(row, col)` debe devolver EXACTAMENTE el valor mostrado en esa celda (mismo formato que el
// <td>, p.ej. money(...)) — el filtro compara sobre eso, no sobre el dato crudo. AND entre columnas.
function useFiltroColumnas<T>(rows: T[], texto: (row: T, col: string) => string) {
  const [filtros, setFiltros] = useState<Record<string, string>>({});
  const [visible, setVisible] = useState(false);
  const activo = Object.values(filtros).some((v) => v.trim() !== '');
  const filtered = useMemo(() => {
    const activos = Object.entries(filtros).filter(([, v]) => v.trim() !== '');
    if (activos.length === 0) return rows;
    return rows.filter((r) => activos.every(([col, val]) => normaliza(texto(r, col)).includes(normaliza(val))));
  }, [rows, filtros, texto]);
  return {
    filtered, visible, activo,
    valor: (col: string) => filtros[col] ?? '',
    setCol: (col: string, v: string) => setFiltros((f) => ({ ...f, [col]: v })),
    limpiar: () => setFiltros({}),
    toggle: () => setVisible((x) => !x),
  };
}
type FiltroApi = ReturnType<typeof useFiltroColumnas>;

// Fila de inputs de texto debajo del encabezado; una celda por columna filtrable (las columnas
// de solo-acción o no filtrables van como celdas vacías vía `leadingBlank`).
function FilaFiltros({ leadingBlank = 0, cols, filtro }: {
  leadingBlank?: number; filtro: FiltroApi;
  cols: { col: string; align?: 'left' | 'right' }[];
}) {
  if (!filtro.visible) return null;
  return (
    <tr className="border-b bg-muted/20">
      {Array.from({ length: leadingBlank }).map((_, i) => <th key={`b${i}`} className="py-1 pr-3" />)}
      {cols.map(({ col, align }) => (
        <th key={col} className="py-1 pr-3">
          <input type="text" value={filtro.valor(col)} onChange={(e) => filtro.setCol(col, e.target.value)}
            placeholder="Filtrar…" aria-label={`Filtrar por ${col}`}
            className={`w-full min-w-[64px] rounded border border-input bg-background px-1.5 py-0.5 text-xs font-normal ${align === 'right' ? 'text-right' : ''} focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`} />
        </th>
      ))}
    </tr>
  );
}
// Botones "Filtrar"/"Limpiar filtros" para el CardHeader de una tabla.
function BotonesFiltro({ filtro }: { filtro: FiltroApi }) {
  return (
    <>
      {filtro.activo && (
        <Button variant="outline" size="sm" onClick={filtro.limpiar}><X className="w-3.5 h-3.5 mr-1" /> Limpiar filtros</Button>
      )}
      <Button variant="outline" size="sm" onClick={filtro.toggle}>
        <Filter className="w-3.5 h-3.5 mr-1" /> {filtro.visible ? 'Ocultar filtros' : 'Filtrar'}
      </Button>
    </>
  );
}

// Fecha local YYYY-MM-DD para un Date (evita el corrimiento UTC).
const toISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Sugiere lunes→sábado (semana bananera de 6 días) para la N-ésima semana del mes.
function sugerirFechas(anio: number, mes: number, semana: number) {
  const primerDia = new Date(anio, mes - 1, 1);
  const diaSemana = primerDia.getDay(); // 0=domingo..6=sábado
  const offsetALunes = (1 - diaSemana + 7) % 7;
  const primerLunes = new Date(anio, mes - 1, 1 + offsetALunes);
  const lunes = new Date(primerLunes); lunes.setDate(primerLunes.getDate() + (semana - 1) * 7);
  const sabado = new Date(lunes); sabado.setDate(lunes.getDate() + 5);
  return { desde: toISO(lunes), hasta: toISO(sabado) };
}

// Sugiere el rango de un PERÍODO BISEMANAL (14 días corridos, lunes a domingo de la 2.ª semana)
// a partir del primer lunes del mes; los períodos se suceden cada 14 días (período 1, 2, 3...).
function sugerirFechasBisemanal(anio: number, mes: number, periodo: number) {
  const primerDia = new Date(anio, mes - 1, 1);
  const diaSemana = primerDia.getDay();
  const offsetALunes = (1 - diaSemana + 7) % 7;
  const primerLunes = new Date(anio, mes - 1, 1 + offsetALunes);
  const inicio = new Date(primerLunes); inicio.setDate(primerLunes.getDate() + (periodo - 1) * 14);
  const fin = new Date(inicio); fin.setDate(inicio.getDate() + 13);
  return { desde: toISO(inicio), hasta: toISO(fin) };
}

// ------------------------------------------------------------ Variante rol ----
// Config que distingue la pantalla Rol Semanal (7 días, hasta 6 semanas/mes) de la
// Rol Bisemanal (14 días, hasta 3 períodos/mes) — mismo componente, mismo motor
// "en firme" (nomina.nom_rol_semanal_* + _rol_semanal_calcular), solo cambia el
// RPC de generación, el tipo de rol y la duración del período.
interface VarianteRol {
  tipo: 'semanal_firme' | 'bisemanal_firme';
  generarRpc: 'nom_rol_semanal_generar' | 'nom_rol_bisemanal_generar';
  titulo: string;
  etiquetaPeriodo: string; // 'Semana' | 'Período'
  diasPeriodo: number; // 7 | 14 — tope de días editables en el roster
  maxPeriodo: number; // 6 | 3 — tope del selector N.º de semana/período
}
const SEMANAL: VarianteRol = {
  tipo: 'semanal_firme', generarRpc: 'nom_rol_semanal_generar', titulo: 'Rol Semanal (banano)',
  etiquetaPeriodo: 'Semana', diasPeriodo: 7, maxPeriodo: 6,
};
const BISEMANAL: VarianteRol = {
  tipo: 'bisemanal_firme', generarRpc: 'nom_rol_bisemanal_generar', titulo: 'Rol Bisemanal (banano)',
  etiquetaPeriodo: 'Período', diasPeriodo: 14, maxPeriodo: 3,
};

const HOY = new Date();
const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

function imprimirRolSemanal(detalle: RolSemDetalle, empresa: string, v: VarianteRol) {
  const rol = detalle.rol;
  if (!rol) return;
  const esBisemanal = v.tipo === 'bisemanal_firme';
  const filas = detalle.empleados.map((e) => {
    const sueldo = rub(e.rubros, 'SUELDO'); const d13 = rub(e.rubros, 'DEC3_SEM');
    const d14 = rub(e.rubros, 'DEC4_SEM'); const fr = rub(e.rubros, 'FR_SEM'); const iess = rub(e.rubros, 'IESS_PERS');
    return `<tr>
      <td>${esc(e.razon_social)}<br><small>${esc(e.identificacion)}</small></td>
      <td>${esc(e.cargo ?? '—')}</td>
      <td>${esc(e.departamento ?? '—')}</td>
      <td class="r">${e.dias}</td>
      <td class="r">${money(sueldo)}</td>
      <td class="r">${money(d13)}</td>
      <td class="r">${money(d14)}</td>
      <td class="r">${money(fr)}</td>
      <td class="r b">${money(e.ingresos)}</td>
      <td class="r">${money(iess)}</td>
      <td class="r">${money(e.anticipo)}</td>
      <td class="r">${money(e.prestamo)}</td>
      <td class="r b">${money(e.egresos)}</td>
      <td class="r b">${money(e.neto)}</td>
    </tr>`;
  }).join('');
  const sum = (f: (e: RolSemEmpleado) => number) => detalle.empleados.reduce((s, e) => s + f(e), 0);
  const filaTotal = `<tr class="tot">
      <td colspan="3">Totales (${detalle.empleados.length})</td>
      <td class="r">${sum((e) => e.dias)}</td>
      <td class="r">${money(sum((e) => rub(e.rubros, 'SUELDO')))}</td>
      <td class="r">${money(sum((e) => rub(e.rubros, 'DEC3_SEM')))}</td>
      <td class="r">${money(sum((e) => rub(e.rubros, 'DEC4_SEM')))}</td>
      <td class="r">${money(sum((e) => rub(e.rubros, 'FR_SEM')))}</td>
      <td class="r">${money(sum((e) => e.ingresos))}</td>
      <td class="r">${money(sum((e) => rub(e.rubros, 'IESS_PERS')))}</td>
      <td class="r">${money(sum((e) => e.anticipo))}</td>
      <td class="r">${money(sum((e) => e.prestamo))}</td>
      <td class="r">${money(sum((e) => e.egresos))}</td>
      <td class="r">${money(sum((e) => e.neto))}</td>
    </tr>`;
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esBisemanal ? 'Rol bisemanal' : 'Rol semanal'} ${esc(rol.codigo)}</title>
    <style>
      *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
      body{margin:0;padding:18px;color:#16211b;font-size:11px}
      .hd{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding-bottom:10px;border-bottom:2px solid #16211b;margin-bottom:10px}
      .co{font-weight:800;font-size:15px}
      .co small{display:block;font-weight:600;color:#61726a;font-size:11px;margin-top:2px}
      .doc{text-align:right;font-size:11px;color:#61726a}
      .doc b{display:block;color:#16211b;font-size:13px;text-transform:uppercase}
      table{width:100%;border-collapse:collapse;font-size:10.5px}
      th,td{padding:4px 6px;border-bottom:1px solid #e4e8e4;text-align:left}
      td.r,th.r{text-align:right;font-variant-numeric:tabular-nums}
      td.b{font-weight:700}
      thead th{background:#eaf4ee;color:#1f7a4d;font-size:9.5px;text-transform:uppercase;letter-spacing:.03em}
      tr.tot td{font-weight:800;border-top:2px solid #16211b;border-bottom:none}
      .nota{margin-top:10px;background:#fbf1dd;border:1px solid #e3c98a;border-radius:8px;padding:8px 10px;font-size:10.5px}
      .nota b{color:#8a5a00}
      @media print{@page{size:A4 landscape;margin:10mm}}
    </style></head><body>
    <div class="hd">
      <div class="co">${esc(empresa)}<small>Rol de pago ${esBisemanal ? 'bisemanal' : 'semanal'} — sector bananero</small></div>
      <div class="doc"><b>ROL DE PAGO ${esBisemanal ? 'BISEMANAL' : 'SEMANAL'}</b>${esc(rol.codigo)} · ${v.etiquetaPeriodo} ${rol.semana} · ${esc(rol.fecha_desde)} al ${esc(rol.fecha_hasta)}</div>
    </div>
    <table>
      <thead><tr>
        <th>Trabajador</th><th>Cargo</th><th>Departamento</th><th class="r">Días</th>
        <th class="r">Sueldo</th><th class="r">Décimo 13</th><th class="r">Décimo 14</th><th class="r">F. Reserva</th>
        <th class="r">Total ingr.</th><th class="r">IESS pers.</th><th class="r">Anticipo</th><th class="r">Préstamo</th>
        <th class="r">Total egr.</th><th class="r">NETO</th>
      </tr></thead>
      <tbody>${filas}${filaTotal}</tbody>
    </table>
    <div class="nota"><b>Nota:</b> el décimo tercero, el décimo cuarto y los fondos de reserva se pagan prorrateados dentro del neto ${esBisemanal ? 'bisemanal' : 'semanal'}, conforme el Código de Trabajo. Este comprobante es el rol de pago EN FIRME ${esBisemanal ? 'del período' : 'de la semana'} (no es un anticipo).</div>
    <script>window.onload=function(){window.print();}</script></body></html>`;
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html); win.document.close();
}

// ------------------------------------------------------------ Fila editable --
const inputCls = 'w-16 rounded border border-input bg-background px-1 py-0.5 text-right text-xs tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const inputMoneyCls = 'w-20 rounded border border-input bg-background px-1 py-0.5 text-right text-xs tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

function FilaRoster({ n, e, editable, onGuardar, guardando, diasMax }: {
  n: number; e: RolSemEmpleado; editable: boolean; guardando: boolean; diasMax: number;
  onGuardar: (v: { dias: number; anticipo: number; prestamo: number }) => void;
}) {
  const [dias, setDias] = useState(String(e.dias));
  const [anticipo, setAnticipo] = useState(String(e.anticipo));
  const [prestamo, setPrestamo] = useState(String(e.prestamo));

  const sueldo = rub(e.rubros, 'SUELDO');
  const d13 = rub(e.rubros, 'DEC3_SEM');
  const d14 = rub(e.rubros, 'DEC4_SEM');
  const fr = rub(e.rubros, 'FR_SEM');
  const iess = rub(e.rubros, 'IESS_PERS');

  const guardar = () => {
    const nd = Math.min(diasMax, Math.max(0, Math.round(Number(dias) || 0)));
    const na = Math.max(0, Number(anticipo) || 0);
    const np = Math.max(0, Number(prestamo) || 0);
    setDias(String(nd)); setAnticipo(String(na)); setPrestamo(String(np));
    if (nd === e.dias && na === Number(e.anticipo) && np === Number(e.prestamo)) return; // sin cambios
    onGuardar({ dias: nd, anticipo: na, prestamo: np });
  };

  return (
    <tr className="border-b last:border-0 hover:bg-muted/40">
      <td className="py-2 pr-3 text-right text-muted-foreground">{n}</td>
      <td className="py-2 pr-3">{e.razon_social}<div className="text-xs text-muted-foreground font-mono">{e.identificacion}</div></td>
      <td className="py-2 pr-3">{e.cargo ?? '—'}</td>
      <td className="py-2 pr-3">{e.departamento ?? '—'}</td>
      <td className="py-2 pr-3 text-right">
        {editable ? (
          <input type="number" min={0} max={diasMax} step={1} className={inputCls} value={dias}
            disabled={guardando}
            onChange={(ev) => setDias(ev.target.value)} onBlur={guardar} />
        ) : <span className="font-mono tabular-nums">{e.dias}</span>}
      </td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sueldo)}</td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d13)}</td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d14)}</td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(fr)}</td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">{money(e.ingresos)}</td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(iess)}</td>
      <td className="py-2 pr-3 text-right">
        {editable ? (
          <input type="number" min={0} step={0.01} className={inputMoneyCls} value={anticipo}
            disabled={guardando}
            onChange={(ev) => setAnticipo(ev.target.value)} onBlur={guardar} />
        ) : <span className="font-mono tabular-nums">{money(e.anticipo)}</span>}
      </td>
      <td className="py-2 pr-3 text-right">
        {editable ? (
          <input type="number" min={0} step={0.01} className={inputMoneyCls} value={prestamo}
            disabled={guardando}
            onChange={(ev) => setPrestamo(ev.target.value)} onBlur={guardar} />
        ) : <span className="font-mono tabular-nums">{money(e.prestamo)}</span>}
      </td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold text-urgente">{money(e.egresos)}</td>
      <td className="py-2 pr-3 text-right font-mono tabular-nums font-bold text-blue-600 dark:text-blue-400">{money(e.neto)}</td>
    </tr>
  );
}

// --------------------------------------------------------- Modal: generar ----
function ModalGenerarSemana({ v, anio, mes, semanaSugerida, onClose, onGenerado }: {
  v: VarianteRol; anio: number; mes: number; semanaSugerida: number; onClose: () => void; onGenerado: (id: string) => void;
}) {
  const esBisemanal = v.tipo === 'bisemanal_firme';
  const [semana, setSemana] = useState(semanaSugerida);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  useEffect(() => {
    const f = esBisemanal ? sugerirFechasBisemanal(anio, mes, semana) : sugerirFechas(anio, mes, semana);
    setDesde(f.desde); setHasta(f.hasta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semana]);

  const generar = useMutation({
    mutationFn: () => nomRpc<RolSemDetalle>(v.generarRpc, esBisemanal
      ? { _anio: anio, _mes: mes, _periodo: semana, _fecha_desde: desde, _fecha_hasta: hasta }
      : { _anio: anio, _mes: mes, _semana: semana, _fecha_desde: desde, _fecha_hasta: hasta }),
    onSuccess: (data) => {
      toast.success(esBisemanal ? `Período ${semana} generado (borrador)` : `Semana ${semana} generada (borrador)`);
      onGenerado(data.rol!.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Generar ${v.etiquetaPeriodo.toLowerCase()}`} onClose={onClose}>
      <div className="space-y-3">
        <Campo label={`N.º de ${v.etiquetaPeriodo.toLowerCase()} (1–${v.maxPeriodo})`}>
          <input type="number" min={1} max={v.maxPeriodo} className={fieldCls} value={semana}
            onChange={(e) => setSemana(Math.min(v.maxPeriodo, Math.max(1, Number(e.target.value) || 1)))} />
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Desde (lunes)"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label={esBisemanal ? 'Hasta (domingo, semana 2)' : 'Hasta (sábado)'}><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        </div>
        <p className="text-xs text-muted-foreground">
          {esBisemanal
            ? 'Se sugiere un período de 14 días (2 semanas, lunes a domingo); puedes ajustar el rango si es necesario. Arma el roster con los empleados de frecuencia bisemanal con contrato vigente en el período.'
            : 'Se sugiere lunes a sábado (semana bananera de 6 días); puedes ajustar el rango si es necesario. Arma el roster con los empleados de frecuencia semanal con contrato vigente en el período.'}
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={generar.isPending} disabled={!desde || !hasta} onClick={() => generar.mutate()}>Generar</Button>
        </div>
      </div>
    </Modal>
  );
}

// --------------------------------------------------------- Modal: pagar ------
function ModalPagar({ titulo, onClose, onConfirmar, cargando }: {
  titulo: string; onClose: () => void; onConfirmar: (fecha: string) => void; cargando: boolean;
}) {
  const [fecha, setFecha] = useState(hoyISO());
  return (
    <Modal title={titulo} onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Fecha de pago"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={cargando} onClick={() => onConfirmar(fecha)}>Confirmar pago</Button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------- Modal: pase de lista --
// Asistencia rápida SIN biométrico (cliente ABANSER): grilla de los empleados del
// rol, filtro por cuadrilla/finca (departamento) + buscador por nombre, input de
// días (0..diasMax) por fila. Escribe con la MISMA RPC que el import masivo.
type MutAsistencia = UseMutationResult<AsistenciaResultado, unknown, { empleado_id?: string; cedula?: string; dias: number }[]>;

function ModalPaseLista({ empleados, diasMax, guardarAsistencia, onClose }: {
  empleados: RolSemEmpleado[]; diasMax: number; guardarAsistencia: MutAsistencia; onClose: () => void;
}) {
  const [valores, setValores] = useState<Record<string, number>>(() =>
    Object.fromEntries(empleados.map((e) => [e.empleado_id, e.dias])));
  const [cuadrilla, setCuadrilla] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const SIN_CUADRILLA = 'Sin cuadrilla';
  const cuadrillas = useMemo(
    () => Array.from(new Set(empleados.map((e) => e.departamento || SIN_CUADRILLA))).sort((a, b) => a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })),
    [empleados],
  );
  const filtrados = useMemo(() => empleados.filter((e) => {
    if (cuadrilla && (e.departamento || SIN_CUADRILLA) !== cuadrilla) return false;
    if (busqueda.trim() && !normaliza(e.razon_social).includes(normaliza(busqueda))) return false;
    return true;
  }), [empleados, cuadrilla, busqueda]);

  const setDia = (empleadoId: string, v: number) => setValores((prev) => ({ ...prev, [empleadoId]: Math.min(diasMax, Math.max(0, Math.round(v))) }));
  const setTodos = (v: number) => setValores((prev) => {
    const next = { ...prev };
    for (const e of filtrados) next[e.empleado_id] = v;
    return next;
  });

  const guardar = () => {
    const items = empleados.map((e) => ({ empleado_id: e.empleado_id, dias: valores[e.empleado_id] ?? e.dias }));
    guardarAsistencia.mutate(items, {
      onSuccess: (r) => { toast.success(`${r.actualizados} empleado(s) actualizados`); onClose(); },
    });
  };

  return (
    <Modal title="Pase de lista" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Todos parten con sus días completos ({diasMax}); solo ajusta a quien tuvo falta.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select className={`${fieldCls} w-56`} value={cuadrilla} onChange={(e) => setCuadrilla(e.target.value)} aria-label="Cuadrilla / finca">
            <option value="">Todas las cuadrillas</option>
            {cuadrillas.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="text" className={`${fieldCls} w-56`} placeholder="Buscar por nombre…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          <div className="flex gap-1 ml-auto">
            <Button variant="outline" size="sm" onClick={() => setTodos(diasMax)}>Todos = máx ({diasMax})</Button>
            <Button variant="outline" size="sm" onClick={() => setTodos(0)}>Todos = 0</Button>
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 pl-3 text-right">N.º</th>
                <th className="py-2 pr-3">Empleado</th>
                <th className="py-2 pr-3">Cuadrilla</th>
                <th className="py-2 pr-3 text-right">Días (máx. {diasMax})</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin empleados para este filtro</td></tr>}
              {filtrados.map((e, i) => {
                const v = valores[e.empleado_id] ?? e.dias;
                const modificado = v !== e.dias;
                return (
                  <tr key={e.empleado_id} className={`border-b last:border-0 hover:bg-muted/40 ${modificado ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                    <td className="py-1.5 pr-3 pl-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3">{e.razon_social}<div className="text-xs text-muted-foreground font-mono">{e.identificacion}</div></td>
                    <td className="py-1.5 pr-3">{e.departamento ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right">
                      <input type="number" min={0} max={diasMax} step={1} className={inputCls} value={v}
                        onChange={(ev) => setDia(e.empleado_id, Number(ev.target.value) || 0)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardarAsistencia.isPending} onClick={guardar}>Guardar asistencia</Button>
        </div>
      </div>
    </Modal>
  );
}

// -------------------------------------------------- Modal: importar asistencia --
// Excel/CSV [cedula, nombre, dias] → nom_rol_semanal_asistencia (mismo RPC del pase
// de lista). "nombre" es solo referencia visual del cliente al llenar la plantilla;
// el match del backend es por cédula.
function ModalImportarAsistencia({ empleados, rolCodigo, diasMax, guardarAsistencia, onClose }: {
  empleados: RolSemEmpleado[]; rolCodigo: string | null; diasMax: number; guardarAsistencia: MutAsistencia; onClose: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [parseando, setParseando] = useState(false);
  const [preview, setPreview] = useState<{ cedula: string; dias: number }[] | null>(null);
  const [resultado, setResultado] = useState<AsistenciaResultado | null>(null);

  const descargarPlantilla = async () => {
    const XLSX = await import('xlsx');
    const headers = ['cedula', 'nombre', 'dias'];
    const rows = empleados.map((e) => [e.identificacion, e.razon_social, e.dias]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Asistencia');
    XLSX.writeFile(wb, `asistencia_${rolCodigo || 'rol'}.xlsx`);
  };

  const onArchivo = async (file: File | null) => {
    if (!file) return;
    setParseando(true); setResultado(null); setPreview(null);
    try {
      const buf = await file.arrayBuffer();
      const XLSX = await import('xlsx');
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' });
      if (rows.length < 2) { toast.error('El archivo no tiene filas de datos'); return; }
      const headers = (rows[0] || []).map(normEncabezado);
      const iCedula = headers.indexOf('CEDULA');
      const iDias = headers.indexOf('DIAS');
      if (iCedula < 0 || iDias < 0) { toast.error('El archivo no tiene las columnas esperadas (cédula, días)'); return; }
      const items: { cedula: string; dias: number }[] = [];
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        if (!row || row.every((c) => c === '' || c == null)) continue;
        const ced = String(row[iCedula] ?? '').trim();
        if (!ced) continue;
        const diasRaw = row[iDias];
        const dias = typeof diasRaw === 'number' ? diasRaw : Number(String(diasRaw).replace(',', '.'));
        if (!Number.isFinite(dias)) continue;
        items.push({ cedula: ced, dias: Math.min(diasMax, Math.max(0, Math.round(dias))) });
      }
      if (items.length === 0) { toast.error('No se detectaron filas válidas (cédula + días)'); return; }
      setPreview(items);
    } catch (e) {
      toast.error(`No se pudo leer el archivo: ${getErrorMessage(e)}`);
    } finally {
      setParseando(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const aplicar = () => {
    if (!preview) return;
    guardarAsistencia.mutate(preview, {
      onSuccess: (r) => { setResultado(r); setPreview(null); toast.success(`${r.actualizados} empleado(s) actualizados`); },
    });
  };

  return (
    <Modal title="Importar asistencia" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Descarga la plantilla, marca los días trabajados por cada cédula y vuelve a subirla.
          Columnas: <span className="font-mono">cedula</span>, <span className="font-mono">nombre</span> (solo referencia), <span className="font-mono">dias</span>.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void descargarPlantilla()}>
            <Download className="w-4 h-4 mr-1" /> Descargar plantilla
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => void onArchivo(e.target.files?.[0] ?? null)} />
          <Button variant="outline" isLoading={parseando} onClick={() => fileRef.current?.click()}>
            <Upload className="w-4 h-4 mr-1" /> Elegir archivo
          </Button>
        </div>

        {preview && (
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm">{preview.length} fila(s) leídas del archivo. ¿Aplicar la asistencia?</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreview(null)}>Descartar</Button>
              <Button size="sm" isLoading={guardarAsistencia.isPending} onClick={aplicar}>Aplicar</Button>
            </div>
          </div>
        )}

        {resultado && (
          <div className="space-y-2">
            <p className="text-sm text-completado">{resultado.actualizados} empleado(s) actualizados.</p>
            {resultado.no_encontrados.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
                <b>{resultado.no_encontrados.length} cédula(s) no encontradas</b> en el rol de esta semana:
                <div className="font-mono mt-1 break-words">{resultado.no_encontrados.join(', ')}</div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </div>
      </div>
    </Modal>
  );
}

// --------------------------------------------------------- Detalle semana ----
function DetalleSemana({ v, rolId, empresa, onEliminado }: {
  v: VarianteRol; rolId: string; empresa: string; onEliminado: () => void;
}) {
  const esBisemanal = v.tipo === 'bisemanal_firme';
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { hasAccessLevel } = usePermissions();
  const [pagarAbierto, setPagarAbierto] = useState(false);
  const [paseListaAbierto, setPaseListaAbierto] = useState(false);
  const [importarAbierto, setImportarAbierto] = useState(false);

  const { data: detalle, isFetching } = useQuery<RolSemDetalle>({
    queryKey: ['nom-rol', rolId],
    queryFn: () => nomRpc<RolSemDetalle>('nom_rol', { _id: rolId }),
    staleTime: 0,
  });

  const invalidarLista = () => void qc.invalidateQueries({ queryKey: ['nom-roles-semanal', v.tipo] });
  const refrescarDetalle = () => void qc.invalidateQueries({ queryKey: ['nom-rol', rolId] });

  // Pase de lista / import: MISMA RPC borra-y-fija días por empleado_id o cédula.
  const guardarAsistencia = useMutation({
    mutationFn: (items: { empleado_id?: string; cedula?: string; dias: number }[]) =>
      nomRpc<AsistenciaResultado>('nom_rol_semanal_asistencia', { _rol_id: rolId, _items: items }),
    onSuccess: () => { refrescarDetalle(); invalidarLista(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardarDias = useMutation({
    mutationFn: (p: { empleado_id: string; dias: number; anticipo: number; prestamo: number }) =>
      nomRpc<RolSemDetalle>('nom_rol_semanal_dias', {
        _rol: rolId, _empleado: p.empleado_id, _dias: p.dias, _anticipo: p.anticipo, _prestamo: p.prestamo,
      }),
    onSuccess: (data) => { qc.setQueryData(['nom-rol', rolId], data); invalidarLista(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const cerrar = useMutation({
    mutationFn: () => nomRpc<RolSemDetalle>('nom_rol_semanal_cerrar', { _id: rolId }),
    onSuccess: (data) => { toast.success(esBisemanal ? 'Período cerrado' : 'Semana cerrada'); qc.setQueryData(['nom-rol', rolId], data); invalidarLista(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: () => nomRpc('nom_rol_eliminar', { _id: rolId }),
    onSuccess: () => { toast.success('Borrador eliminado'); invalidarLista(); onEliminado(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const contabilizar = useMutation({
    mutationFn: () => nomRpc<{ numero: number; reemplazado: boolean }>('nom_rol_semanal_contabilizar', { _rol_id: rolId }),
    onSuccess: (r) => {
      toast.success(`Asiento contable N.º ${r.numero} ${r.reemplazado ? 'regenerado' : 'creado'} (Contabilidad → Asientos)`);
      refrescarDetalle(); invalidarLista();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pagar = useMutation({
    mutationFn: (fecha: string) => nomRpc<{ id: string; estado: string }>('nom_rol_pagar', { _id: rolId, _fecha: fecha }),
    onSuccess: () => { toast.success(esBisemanal ? 'Período marcado como pagado' : 'Semana marcada como pagada'); setPagarAbierto(false); refrescarDetalle(); invalidarLista(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reabrir = useMutation({
    mutationFn: async () => {
      const v2 = await dlg.pedir(`Motivo de la reapertura ${esBisemanal ? 'del período' : 'de la semana'}`, [{ key: 'motivo', label: 'Motivo (queda registrado en la bitácora)', minimo: 1 }]);
      if (v2 === null) return Promise.reject(new Error('Reapertura cancelada: falta el motivo'));
      return nomRpc('nom_rol_reabrir', { _id: rolId, _motivo: v2.motivo.trim() });
    },
    onSuccess: () => { toast.success(esBisemanal ? 'Período reabierto (borrador)' : 'Semana reabierta (borrador)'); refrescarDetalle(); invalidarLista(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const getVal = useCallback((r: RolSemEmpleado, key: string): string | number | null => {
    switch (key) {
      case 'empleado': return r.razon_social;
      case 'cargo': return r.cargo;
      case 'departamento': return r.departamento;
      case 'dias': return Number(r.dias);
      case 'sueldo': return rub(r.rubros, 'SUELDO');
      case 'd13': return rub(r.rubros, 'DEC3_SEM');
      case 'd14': return rub(r.rubros, 'DEC4_SEM');
      case 'fr': return rub(r.rubros, 'FR_SEM');
      case 'ingresos': return Number(r.ingresos);
      case 'iess': return rub(r.rubros, 'IESS_PERS');
      case 'anticipo': return Number(r.anticipo);
      case 'prestamo': return Number(r.prestamo);
      case 'egresos': return Number(r.egresos);
      case 'neto': return Number(r.neto);
      default: return null;
    }
  }, []);
  // Valor MOSTRADO por columna (mismo formato que el <td>) — sobre eso filtra useFiltroColumnas.
  const textoCol = useCallback((r: RolSemEmpleado, key: string): string => {
    switch (key) {
      case 'empleado': return `${r.razon_social} ${r.identificacion}`;
      case 'cargo': return r.cargo ?? '—';
      case 'departamento': return r.departamento ?? '—';
      case 'dias': return String(r.dias);
      case 'sueldo': return money(rub(r.rubros, 'SUELDO'));
      case 'd13': return money(rub(r.rubros, 'DEC3_SEM'));
      case 'd14': return money(rub(r.rubros, 'DEC4_SEM'));
      case 'fr': return money(rub(r.rubros, 'FR_SEM'));
      case 'ingresos': return money(r.ingresos);
      case 'iess': return money(rub(r.rubros, 'IESS_PERS'));
      case 'anticipo': return money(r.anticipo);
      case 'prestamo': return money(r.prestamo);
      case 'egresos': return money(r.egresos);
      case 'neto': return money(r.neto);
      default: return '';
    }
  }, []);
  const filtro = useFiltroColumnas<RolSemEmpleado>(detalle?.empleados ?? [], textoCol);
  const sort = useSort<RolSemEmpleado>(filtro.filtered, getVal);

  if (isFetching && !detalle) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  const rol = detalle?.rol;
  if (!rol || !detalle) return null;
  const editable = rol.estado === 'borrador';
  const empleados = sort.sorted;

  const sum = (f: (e: RolSemEmpleado) => number) => empleados.reduce((s, e) => s + f(e), 0);

  return (
    <div className="space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">
            {v.etiquetaPeriodo} {rol.semana} <span className="font-mono text-sm text-muted-foreground">{rol.codigo}</span>
          </h2>
          <span className="text-sm text-muted-foreground">{rol.fecha_desde} al {rol.fecha_hasta}</span>
          <EstadoRol estado={rol.estado} />
          {detalle.asiento && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-completado-soft text-completado">
              Asiento N.º {detalle.asiento.numero} ({detalle.asiento.fecha.slice(0, 10)})
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => imprimirRolSemanal(detalle, empresa, v)}>
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          {hasAccessLevel(3) && (
            <>
              <span title={editable ? undefined : `Reabre ${esBisemanal ? 'el período' : 'la semana'} para registrar asistencia`}>
                <Button variant="outline" disabled={!editable} onClick={() => setPaseListaAbierto(true)}>
                  <ClipboardList className="w-4 h-4 mr-1" /> Pase de lista
                </Button>
              </span>
              <span title={editable ? undefined : `Reabre ${esBisemanal ? 'el período' : 'la semana'} para registrar asistencia`}>
                <Button variant="outline" disabled={!editable} onClick={() => setImportarAbierto(true)}>
                  <FileSpreadsheet className="w-4 h-4 mr-1" /> Importar asistencia
                </Button>
              </span>
            </>
          )}
          {editable && (
            <>
              <Button isLoading={cerrar.isPending}
                onClick={async () => { if (await dlg.confirmar(`¿Cerrar ${esBisemanal ? 'el período' : 'la semana'}?`, { detalle: 'Ya no se podrá editar días/anticipo/préstamo.', aceptar: 'Cerrar' })) cerrar.mutate(); }}>
                <Lock className="w-4 h-4 mr-1" /> Cerrar {v.etiquetaPeriodo.toLowerCase()}
              </Button>
              <Button variant="destructive" isLoading={eliminar.isPending}
                onClick={async () => { if (await dlg.confirmar('¿Eliminar este borrador?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(); }}>
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
              </Button>
            </>
          )}
          {rol.estado === 'cerrado' && (
            <>
              <Button variant="outline" isLoading={contabilizar.isPending}
                onClick={async () => {
                  const ya = !!detalle.asiento;
                  const ok = await dlg.confirmar(ya ? '¿Regenerar el asiento?' : '¿Contabilizar?', {
                    detalle: ya ? `Ya existe el asiento N.º ${detalle.asiento!.numero}.` : 'Se creará el asiento de causación (MO por etapa productiva).',
                    aceptar: ya ? 'Regenerar' : 'Contabilizar',
                  });
                  if (ok) contabilizar.mutate();
                }}>
                <BookOpen className="w-4 h-4 mr-1" /> {detalle.asiento ? 'Re-contabilizar' : 'Contabilizar'}
              </Button>
              <Button isLoading={pagar.isPending} onClick={() => setPagarAbierto(true)}>
                <Wallet className="w-4 h-4 mr-1" /> Marcar pagado
              </Button>
              <Button variant="outline" isLoading={reabrir.isPending}
                onClick={async () => { if (await dlg.confirmar('¿Reabrir?', { detalle: `Reabrir devuelve ${esBisemanal ? 'el período' : 'la semana'} a BORRADOR y revierte el cierre (y el asiento, si existe).`, aceptar: 'Reabrir' })) reabrir.mutate(); }}>
                <RotateCcw className="w-4 h-4 mr-1" /> Reabrir
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: 'Empleados', v: String(rol.n_empleados) },
          { k: 'Ingresos', v: money(rol.total_ingresos) },
          { k: 'Egresos', v: money(rol.total_egresos) },
          { k: 'Neto a pagar', v: money(rol.total_neto) },
        ].map((x) => (
          <Card key={x.k}><CardContent className="py-3">
            <div className="text-xs text-muted-foreground">{x.k}</div>
            <div className="text-lg font-semibold font-mono tabular-nums">{x.v}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
          <CardTitle className="text-base">{esBisemanal ? `Roster del período (${empleados.length})` : `Roster de la semana (${empleados.length})`}</CardTitle>
          <div className="flex items-center gap-2"><BotonesFiltro filtro={filtro} /></div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N.º</th>
                <SortTh col="empleado" sort={sort}>Empleado</SortTh>
                <SortTh col="cargo" sort={sort}>Cargo</SortTh>
                <SortTh col="departamento" sort={sort}>Departamento</SortTh>
                <SortTh col="dias" sort={sort} align="right" tip="Días pagados del período. En modelo jornal, el descanso va incorporado en la tarifa del día; una falta resta el jornal y su proporción de descanso.">Días</SortTh>
                <SortTh col="sueldo" sort={sort} align="right" tip="Jornal × días. Jornal = sueldo ÷ divisor de días (mensual = /30; jornal bananero = /26, con el descanso incorporado en la tarifa).">Sueldo</SortTh>
                <SortTh col="d13" sort={sort} align="right" tip="Décimo tercero (Art. 111): base ÷ 12. Base = sueldo ganado + horas extra. Se paga prorrateado en el neto (mensualizado).">Décimo 13</SortTh>
                <SortTh col="d14" sort={sort} align="right" tip="Décimo cuarto (Art. 113): SBU ÷ 52 × (horas de jornada ÷ 40). Prorrateado por jornada, no por asistencia. Se paga en el neto.">Décimo 14</SortTh>
                <SortTh col="fr" sort={sort} align="right" tip="Fondos de reserva (Art. 196): 8,33% × base, solo desde el año de servicio cumplido. Se paga mensualizado en el neto.">F. Reserva</SortTh>
                <SortTh col="ingresos" sort={sort} align="right" tip="Total ingresos = Sueldo + Décimo 13 + Décimo 14 + Fondos de reserva (+ horas extra si aplican). Las vacaciones se provisionan aparte, no entran al neto.">Total ingr.</SortTh>
                <SortTh col="iess" sort={sort} align="right" tip="Aporte personal IESS: 9,45% × base (sueldo + horas extra). Se descuenta al trabajador.">IESS pers.</SortTh>
                <SortTh col="anticipo" sort={sort} align="right" tip="Anticipos aprobados a descontar esta semana.">Anticipo</SortTh>
                <SortTh col="prestamo" sort={sort} align="right" tip="Cuota del préstamo interno a descontar esta semana.">Préstamo</SortTh>
                <SortTh col="egresos" sort={sort} align="right" tip="Total egresos = IESS personal + Anticipo + Préstamo.">Total egr.</SortTh>
                <SortTh col="neto" sort={sort} align="right" tip="Neto a pagar = Total ingresos − Total egresos.">Neto</SortTh>
              </tr>
              <FilaFiltros leadingBlank={1} filtro={filtro} cols={[
                { col: 'empleado' }, { col: 'cargo' }, { col: 'departamento' },
                { col: 'dias', align: 'right' }, { col: 'sueldo', align: 'right' },
                { col: 'd13', align: 'right' }, { col: 'd14', align: 'right' }, { col: 'fr', align: 'right' },
                { col: 'ingresos', align: 'right' }, { col: 'iess', align: 'right' },
                { col: 'anticipo', align: 'right' }, { col: 'prestamo', align: 'right' },
                { col: 'egresos', align: 'right' }, { col: 'neto', align: 'right' },
              ]} />
            </thead>
            <tbody>
              {empleados.length === 0 && <tr><td colSpan={15} className="py-8 text-center text-muted-foreground">Sin empleados en el roster</td></tr>}
              {empleados.map((e, i) => (
                <FilaRoster key={e.id} n={i + 1} e={e} editable={editable} guardando={guardarDias.isPending} diasMax={v.diasPeriodo}
                  onGuardar={(vals) => guardarDias.mutate({ empleado_id: e.empleado_id, ...vals })} />
              ))}
            </tbody>
            {empleados.length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={4}>Totales ({empleados.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{sum((e) => e.dias)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sum((e) => rub(e.rubros, 'SUELDO')))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sum((e) => rub(e.rubros, 'DEC3_SEM')))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sum((e) => rub(e.rubros, 'DEC4_SEM')))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sum((e) => rub(e.rubros, 'FR_SEM')))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400">{money(sum((e) => e.ingresos))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sum((e) => rub(e.rubros, 'IESS_PERS')))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sum((e) => e.anticipo))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sum((e) => e.prestamo))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-urgente">{money(sum((e) => e.egresos))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-blue-600 dark:text-blue-400">{money(sum((e) => e.neto))}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {pagarAbierto && (
        <ModalPagar titulo={esBisemanal ? 'Marcar período como pagado' : 'Marcar semana como pagada'}
          onClose={() => setPagarAbierto(false)} cargando={pagar.isPending}
          onConfirmar={(fecha) => pagar.mutate(fecha)} />
      )}

      {paseListaAbierto && (
        <ModalPaseLista empleados={detalle.empleados} diasMax={v.diasPeriodo}
          guardarAsistencia={guardarAsistencia} onClose={() => setPaseListaAbierto(false)} />
      )}

      {importarAbierto && (
        <ModalImportarAsistencia empleados={detalle.empleados} rolCodigo={rol.codigo} diasMax={v.diasPeriodo}
          guardarAsistencia={guardarAsistencia} onClose={() => setImportarAbierto(false)} />
      )}
    </div>
  );
}

// ------------------------------------------------------------ Consolidado ----
const SIN_CENTRO_COSTO = 'Sin centro de costo';
interface GrupoCC { nombre: string; filas: ConsolidadoEmpleado[]; ganado: number; iess_personal: number; iess_patronal: number; neto: number }

function ConsolidadoMensual({ v, anio, mes }: { v: VarianteRol; anio: number; mes: number }) {
  const esBisemanal = v.tipo === 'bisemanal_firme';
  const { data, isFetching } = useQuery<ConsolidadoData>({
    queryKey: ['nom-rol-semanal-consolidado', v.tipo, anio, mes],
    queryFn: () => nomRpc<ConsolidadoData>('nom_rol_semanal_consolidado', { _anio: anio, _mes: mes, _tipo: v.tipo }),
  });
  const empleados = data?.empleados ?? [];
  const [agrupar, setAgrupar] = useState(true);

  const getVal = useCallback((r: ConsolidadoEmpleado, key: string): string | number | null => {
    switch (key) {
      case 'nombre': return r.nombre;
      case 'cedula': return r.cedula;
      case 'centro_costo': return r.centro_costo ?? null;
      case 'semanas': return r.semanas;
      case 'ganado': return Number(r.ganado);
      case 'iess_personal': return Number(r.iess_personal);
      case 'iess_patronal': return Number(r.iess_patronal);
      case 'neto': return Number(r.neto);
      default: return null;
    }
  }, []);
  // Valor MOSTRADO por columna (mismo formato que el <td>) — sobre eso filtra useFiltroColumnas.
  const textoCol = useCallback((r: ConsolidadoEmpleado, key: string): string => {
    switch (key) {
      case 'nombre': return `${r.nombre} ${r.cedula ?? ''}`;
      case 'cedula': return r.cedula ?? '—';
      case 'centro_costo': return r.centro_costo ?? SIN_CENTRO_COSTO;
      case 'semanas': return String(r.semanas);
      case 'ganado': return money(r.ganado);
      case 'iess_personal': return money(r.iess_personal);
      case 'iess_patronal': return money(r.iess_patronal);
      case 'neto': return money(r.neto);
      default: return '';
    }
  }, []);
  const filtro = useFiltroColumnas<ConsolidadoEmpleado>(empleados, textoCol);
  const sort = useSort<ConsolidadoEmpleado>(filtro.filtered, getVal);
  const filas = sort.sorted;

  // Agrupado por centro de costo (finca): subtotales + colapsable. El orden DENTRO de cada
  // grupo respeta el useSort de arriba (se agrupa sobre `filas`, que ya viene ordenada);
  // los grupos se ordenan alfabéticamente y "Sin centro de costo" siempre al final.
  const grupos = useMemo<GrupoCC[]>(() => {
    const m = new Map<string, GrupoCC>();
    for (const r of filas) {
      const k = r.centro_costo || SIN_CENTRO_COSTO;
      let g = m.get(k);
      if (!g) { g = { nombre: k, filas: [], ganado: 0, iess_personal: 0, iess_patronal: 0, neto: 0 }; m.set(k, g); }
      g.filas.push(r);
      g.ganado += Number(r.ganado || 0); g.iess_personal += Number(r.iess_personal || 0);
      g.iess_patronal += Number(r.iess_patronal || 0); g.neto += Number(r.neto || 0);
    }
    return Array.from(m.values()).sort((a, b) => {
      if (a.nombre === SIN_CENTRO_COSTO) return 1;
      if (b.nombre === SIN_CENTRO_COSTO) return -1;
      return a.nombre.localeCompare(b.nombre, 'es', { numeric: true, sensitivity: 'base' });
    });
  }, [filas]);
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());
  const toggleGrupo = (n: string) => setColapsados((s) => { const x = new Set(s); x.has(n) ? x.delete(n) : x.add(n); return x; });
  const expandirTodo = () => setColapsados(new Set());
  const contraerTodo = () => setColapsados(new Set(grupos.map((g) => g.nombre)));

  const totalGanado = filas.reduce((s, e) => s + Number(e.ganado), 0);
  const totalIessPersonal = filas.reduce((s, e) => s + Number(e.iess_personal), 0);
  const totalIessPatronal = filas.reduce((s, e) => s + Number(e.iess_patronal), 0);
  const totalNeto = filas.reduce((s, e) => s + Number(e.neto), 0);
  const nCols = agrupar ? 7 : 9; // Empleado+Cédula+Semanas+4 montos (agrupado, sin N.º/Centro) | + N.º + Centro de costo

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
        <CardTitle className="text-base">Consolidado mensual — {MESES[mes - 1]} {anio} ({filas.length})</CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
            <input type="checkbox" checked={agrupar} onChange={(e) => setAgrupar(e.target.checked)} />
            Agrupar por centro de costo
          </label>
          {agrupar && grupos.length > 0 && (
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={expandirTodo}>Expandir todo</Button>
              <Button variant="outline" size="sm" onClick={contraerTodo}>Contraer todo</Button>
            </div>
          )}
          <BotonesFiltro filtro={filtro} />
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto space-y-3">
        <p className="text-xs text-muted-foreground">
          {esBisemanal
            ? 'Base para la planilla IESS mensual / SUT: suma de todos los períodos pagados del mes por empleado.'
            : 'Base para la planilla IESS mensual / SUT: suma de todas las semanas pagadas del mes por empleado.'}
        </p>
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              {!agrupar && <th className="py-2 pr-3 text-right">N.º</th>}
              <SortTh col="nombre" sort={sort}>Empleado</SortTh>
              <SortTh col="cedula" sort={sort}>Cédula</SortTh>
              {!agrupar && <SortTh col="centro_costo" sort={sort}>Centro de costo</SortTh>}
              <SortTh col="semanas" sort={sort} align="right">{esBisemanal ? 'Períodos' : 'Semanas'}</SortTh>
              <SortTh col="ganado" sort={sort} align="right">Ganado</SortTh>
              <SortTh col="iess_personal" sort={sort} align="right">IESS personal</SortTh>
              <SortTh col="iess_patronal" sort={sort} align="right">IESS patronal</SortTh>
              <SortTh col="neto" sort={sort} align="right">Neto</SortTh>
            </tr>
            <FilaFiltros leadingBlank={agrupar ? 0 : 1} filtro={filtro} cols={[
              { col: 'nombre' }, { col: 'cedula' },
              ...(agrupar ? [] : [{ col: 'centro_costo' }]),
              { col: 'semanas', align: 'right' as const }, { col: 'ganado', align: 'right' as const },
              { col: 'iess_personal', align: 'right' as const }, { col: 'iess_patronal', align: 'right' as const },
              { col: 'neto', align: 'right' as const },
            ]} />
          </thead>
          <tbody>
            {filas.length === 0 && !isFetching && <tr><td colSpan={nCols} className="py-8 text-center text-muted-foreground">{esBisemanal ? 'Sin períodos pagados este mes' : 'Sin semanas pagadas en el período'}</td></tr>}
            {!agrupar && filas.map((e, i) => (
              <tr key={e.empleado_id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-3">{e.nombre}</td>
                <td className="py-2 pr-3 font-mono">{e.cedula ?? '—'}</td>
                <td className="py-2 pr-3">{e.centro_costo ?? SIN_CENTRO_COSTO}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums">{e.semanas}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400">{money(e.ganado)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums text-urgente">{money(e.iess_personal)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(e.iess_patronal)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold text-blue-600 dark:text-blue-400">{money(e.neto)}</td>
              </tr>
            ))}
            {agrupar && grupos.map((g) => {
              const abierta = !colapsados.has(g.nombre);
              return (
                <Fragment key={g.nombre}>
                  <tr className="border-b bg-muted/40 cursor-pointer hover:bg-muted/60" onClick={() => toggleGrupo(g.nombre)}>
                    <td className="py-2 pr-3 font-medium" colSpan={2}>
                      <span className="inline-flex items-center gap-1">
                        {abierta ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        {g.nombre} <span className="text-xs text-muted-foreground">({g.filas.length})</span>
                      </span>
                    </td>
                    <td className="py-2 pr-3"></td>
                    <td className="py-2 pr-3 text-right font-semibold text-emerald-600 dark:text-emerald-400">{money(g.ganado)}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-urgente">{money(g.iess_personal)}</td>
                    <td className="py-2 pr-3 text-right font-semibold">{money(g.iess_patronal)}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-blue-600 dark:text-blue-400">{money(g.neto)}</td>
                  </tr>
                  {abierta && g.filas.map((e) => (
                    <tr key={e.empleado_id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 pr-3 pl-7">{e.nombre}</td>
                      <td className="py-2 pr-3 font-mono">{e.cedula ?? '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{e.semanas}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400">{money(e.ganado)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-urgente">{money(e.iess_personal)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(e.iess_patronal)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold text-blue-600 dark:text-blue-400">{money(e.neto)}</td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          {filas.length > 0 && (
            <tfoot>
              <tr className="border-t-2 font-semibold bg-muted/30">
                <td className="py-2 pr-3" colSpan={agrupar ? 3 : 5}>Totales ({filas.length})</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400">{money(totalGanado)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums text-urgente">{money(totalIessPersonal)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totalIessPatronal)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums text-blue-600 dark:text-blue-400">{money(totalNeto)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------- Main ------
function RolSemanalBase({ v }: { v: VarianteRol }) {
  const esBisemanal = v.tipo === 'bisemanal_firme';
  const qc = useQueryClient();
  const { user } = useAuth();
  const empresa = user?.empresa?.nombre ?? 'Empresa';
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [vista, setVista] = useState<'semanas' | 'consolidado'>('semanas');
  const [selId, setSelId] = useState<string | null>(null);
  const [abrirGenerar, setAbrirGenerar] = useState(false);

  const { data: semanas = [], isFetching } = useQuery<RolSemanaRow[]>({
    queryKey: ['nom-roles-semanal', v.tipo, anio, mes],
    queryFn: () => nomRpc<RolSemanaRow[]>('nom_roles_semanal', { _anio: anio, _mes: mes, _tipo: v.tipo }),
    staleTime: 0,
  });

  // Prefetch de detalle para las semanas NO borrador, solo para saber si tienen
  // asiento (nom_roles_semanal no trae ese dato) — reutiliza la misma query-key
  // ['nom-rol', id] que usa el detalle, así al abrir la semana no se repite el fetch.
  const noBorrador = semanas.filter((s) => s.estado !== 'borrador');
  const asientoQueries = useQueries({
    queries: noBorrador.map((s) => ({
      queryKey: ['nom-rol', s.id],
      queryFn: () => nomRpc<RolSemDetalle>('nom_rol', { _id: s.id }),
      staleTime: 30_000,
    })),
  });
  const tieneAsiento = (id: string) => {
    const idx = noBorrador.findIndex((s) => s.id === id);
    return idx >= 0 ? !!asientoQueries[idx]?.data?.asiento : false;
  };

  const getVal = useCallback((r: RolSemanaRow, key: string): string | number | null => {
    switch (key) {
      case 'semana': return r.semana;
      case 'rango': return r.fecha_desde;
      case 'estado': return r.estado;
      case 'n_empleados': return r.n_empleados;
      case 'ingresos': return Number(r.total_ingresos);
      case 'egresos': return Number(r.total_egresos);
      case 'neto': return Number(r.total_neto);
      default: return null;
    }
  }, []);
  const sort = useSort<RolSemanaRow>(semanas, getVal);
  const filas = sort.sorted;

  const periodosExistentes = new Set(semanas.map((s) => s.semana));
  const semanaSugerida = Array.from({ length: v.maxPeriodo }, (_, i) => i + 1).find((n) => !periodosExistentes.has(n)) ?? 1;

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">{v.titulo}</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-36`} value={mes} onChange={(e) => { setMes(Number(e.target.value)); setSelId(null); }} aria-label="Mes">
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => { setAnio(Number(e.target.value)); setSelId(null); }} aria-label="Año" />
        </div>
      </div>

      <div className="flex gap-2 border-b">
        <button className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${vista === 'semanas' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setVista('semanas')}>
          <Calendar className="w-4 h-4 inline mr-1" /> {v.etiquetaPeriodo}s del mes
        </button>
        <button className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${vista === 'consolidado' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setVista('consolidado')}>
          <Layers className="w-4 h-4 inline mr-1" /> Consolidado mensual
        </button>
      </div>

      {vista === 'consolidado' && <ConsolidadoMensual v={v} anio={anio} mes={mes} />}

      {vista === 'semanas' && (
        <>
          {!selId && (
            <Card>
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">{v.etiquetaPeriodo}s de {MESES[mes - 1]} {anio} ({filas.length})</CardTitle>
                <Button onClick={() => setAbrirGenerar(true)}><Plus className="w-4 h-4 mr-1" /> Generar {v.etiquetaPeriodo.toLowerCase()}</Button>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N.º</th>
                    <SortTh col="semana" sort={sort} align="right">{v.etiquetaPeriodo}</SortTh>
                    <SortTh col="rango" sort={sort}>Rango</SortTh>
                    <SortTh col="estado" sort={sort}>Estado</SortTh>
                    <SortTh col="n_empleados" sort={sort} align="right">Empleados</SortTh>
                    <SortTh col="ingresos" sort={sort} align="right">Ingresos</SortTh>
                    <SortTh col="egresos" sort={sort} align="right">Egresos</SortTh>
                    <SortTh col="neto" sort={sort} align="right">Neto</SortTh>
                    <th className="py-2 pr-3 text-center">Asiento</th>
                  </tr></thead>
                  <tbody>
                    {filas.length === 0 && !isFetching && (
                      <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">
                        {esBisemanal ? 'Sin períodos generados este mes. Usa «Generar período».' : 'Sin semanas generadas este mes. Usa «Generar semana».'}
                      </td></tr>
                    )}
                    {filas.map((s, i) => (
                      <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setSelId(s.id)}
                          role="button" tabIndex={0} onKeyDown={(ev) => { if (ev.key === 'Enter') setSelId(s.id); }}>
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3 font-mono">{s.semana}</td>
                        <td className="py-2 pr-3">{s.fecha_desde} al {s.fecha_hasta}</td>
                        <td className="py-2 pr-3"><EstadoRol estado={s.estado} /></td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{s.n_empleados}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(s.total_ingresos)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(s.total_egresos)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(s.total_neto)}</td>
                        <td className="py-2 pr-3 text-center">
                          {s.estado === 'borrador' ? <span className="text-muted-foreground">—</span>
                            : tieneAsiento(s.id) ? <span className="text-completado">Sí</span> : <span className="text-muted-foreground">No</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {filas.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 font-semibold bg-muted/30">
                        <td className="py-2 pr-3" colSpan={4}>Totales ({filas.length})</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{filas.reduce((s, r) => s + r.n_empleados, 0)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(filas.reduce((s, r) => s + Number(r.total_ingresos), 0))}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(filas.reduce((s, r) => s + Number(r.total_egresos), 0))}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(filas.reduce((s, r) => s + Number(r.total_neto), 0))}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </CardContent>
            </Card>
          )}

          {selId && (
            <div className="space-y-3">
              <Button variant="outline" onClick={() => setSelId(null)}><ArrowLeft className="w-4 h-4 mr-1" /> Volver al listado</Button>
              <DetalleSemana v={v} rolId={selId} empresa={empresa} onEliminado={() => setSelId(null)} />
            </div>
          )}
        </>
      )}

      {abrirGenerar && (
        <ModalGenerarSemana v={v} anio={anio} mes={mes} semanaSugerida={semanaSugerida}
          onClose={() => setAbrirGenerar(false)}
          onGenerado={(id) => {
            setAbrirGenerar(false);
            void qc.invalidateQueries({ queryKey: ['nom-roles-semanal', v.tipo] });
            setSelId(id);
          }} />
      )}
    </div>
  );
}

export function RolSemanal() { return <RolSemanalBase v={SEMANAL} />; }
export function RolBisemanal() { return <RolSemanalBase v={BISEMANAL} />; }
